import { Box, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import ClinicApi from "api/master/clinic.api";
import PromotionsApi from "api/promotion/promotion.api";
import ReportApi from "api/report.api";
import HeaderCard from "component/CardCustom/HeaderCard";
import InputSecondNewRangePicker from "component/Input/InputSecondNewRangePicker";
import Loading from "component/Loading";
import _ from "lodash";
import moment from "moment";
import DropdownButton from "new-components/buttons/dropdown-button";
import MainButton from "new-components/buttons/main-button";
import SelectInput from "new-components/inputs/select-input";
import TextInput from "new-components/inputs/text-input";
import PaginationTable from "new-components/pagination/pagination";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getBranch, numberFormat, toBuddhistYear } from "utils/app.utils";
import { dateTimeToView } from "utils/date.utils";
import { UseSnCode } from "utils/useSnCode";
import { exportPromotionUsedToExcel, PromotionUsedReport } from "./export-to-excel-promotion";

export default function PromotionUsed() {
    const { t } = useTranslation()
    const momentNowRange = [moment().startOf('month').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
    const momentDate = () => momentNowRange
    const componentRef = useRef<HTMLDivElement>(null);
    const [promotionList, setPromotionList] = useState([])
    const [promotionExport, setPromotionExport] = useState([])
    const [promotionType, setPromotionType] = useState<any>([])
    const [promotionTypeId, setPromotionTypeId] = useState("ALL")
    const [optionPromotion, setOptionPromotion] = useState<any>([])
    const [promotionId, setPromotionId] = useState("ALL")
    const [promotion, setPromotion] = useState([])
    const [page, setPage] = useState(1)
    const [pageLimit, setPageLimit] = useState(10)
    const [rowCount, setRowCount] = useState(0)
    const [search, setSearch] = useState('')
    const [branch, setBranch] = useState<any>([])
    const [branchId, setBranchId] = useState<any>(getBranch())
    const [type, setType] = useState('EXTRA,DISCOUNT')
    const [typeName, setTypeName] = useState('')
    const [loading, setLoading] = useState<boolean>(true)
    const [rangeDate, setRangeDate] = useState<string[]>(momentDate)
    const [snType, setSnType] = useState(false)

    const fetchPromotionList = async () => {
        setLoading(true)
        const condition = {
            page: page,
            pageLimit: pageLimit,
            sortBy: 'approvalId',
            sortType: 'ASC',
            branchId: branchId,
            search: search,
            type: type,
            promotionTypeId: promotionTypeId === "ALL" ? null : promotionTypeId,
            promotionId: promotionId === "ALL" ? null : promotionId,
            dateStart: rangeDate[0],
            dateEnd: rangeDate[1]
        }
        const resp: any = await ReportApi.reportPromotion(condition)
        const respExport: any = await ReportApi.reportPromotion({ ...condition, export: 1 })
        if (resp.status === 200) {
            setRowCount(resp?.headers['x-total'] || 0)
            setPromotionList(resp.data?.rows)
            setPromotionExport(respExport.data?.rows)
            setPromotion(resp.data?.group)
        } else {
            setPromotionList([])
            setPromotionExport([])
            setPromotion([])
        }
        setLoading(false)
    }

    const fetchBranch = async () => {
        ClinicApi.findAllBranches()
            .then(({ data }) => setBranch(_.map(data, (item) => ({ value: item.branchId, name: item.branchName || item.branchNameEn })))).catch((e) => { return })
    }

    const fetchPromotionType = async () => {
        PromotionsApi.findAllType({
            page: 1,
            pageLimit: 100,
        }).then(({ data }) => setPromotionType(_.map(data, (item) => ({ value: item.promotionTypeId, name: item.promotionTypeName })))).catch((e) => { return })
    }

    const fetchPromotion = async () => {
        PromotionsApi.findPromotionList({
            promotionTypeId: promotionTypeId === "ALL" ? null : promotionTypeId
        }).then(({ data }) => setOptionPromotion(_.map(data, (item) => ({ value: item.promotionId, name: item.promotionName })))).catch((e) => { return })
    }

    const handleSnCode = useCallback(() => {
        const fetchData = async () => {
            const sn = await UseSnCode()
            setSnType(sn)
        }
        fetchData()
    }, [snType])

    useEffect(() => {
        fetchPromotionList()
    }, [page, pageLimit])

    useEffect(() => {
        fetchBranch()
        fetchPromotionType()
        fetchPromotion()
        handleSnCode()
    }, [])

    const handleExport = () => {

        const report: PromotionUsedReport = {
            branchName: `${_.get(_.find(branch, { value: branchId }), 'name')}`,
            dateStart: rangeDate[0] ? toBuddhistYear(moment(rangeDate[0]), 'DD/MM/YYYY') : '-',
            dateEnd: rangeDate[1] ? toBuddhistYear(moment(rangeDate[1]), 'DD/MM/YYYY') : '-',
            typeFilter: promotionTypeId === "ALL" ? t('REPORT.FILTER.ALL') : `${_.get(_.find(promotionType, { value: promotionTypeId }), 'name')}`,
            promoFilter: promotionId === "ALL" ? t('REPORT.FILTER.ALL') : `${_.get(_.find(optionPromotion, { value: promotionId }), 'name')}`,
            typePromotion: typeName === "" ? t('REPORT.FILTER.ALL') : typeName,
            includeSn: snType,
            rows: promotionExport,
            groups: promotion,
            t
        }
        exportPromotionUsedToExcel(report)
    }

    return (
        <Box>
            <Loading show={loading} />
            <HeaderCard text={t('REPORT.TABLE.PROMOTION_USED.TITLE')} />
            <Box paddingX="24px" paddingY="16px">
                <Grid container spacing="16px" >
                    <Grid container spacing="16px" pt="16px" >
                        <Grid item xs={12} sm={6} md={3} xl={2}>
                            <TextInput onChange={(e) => setSearch(e.target.value)} fullWidth label={t('REPORT.TABLE.PROMOTION_USED.SEARCH')} value={search} />
                        </Grid>
                        <Grid xs={12} sm={6} item md={3} xl={2}>
                            <SelectInput onChange={(e) => setBranchId(e.target.value)} value={branchId} label={t('REPORT.FILTER.BRANCH')} options={branch} defaultValue={branch?.[0]?.branchId} />
                        </Grid>
                        <Grid xs={12} sm={6} item md={3} xl={2}>
                            <SelectInput onChange={(e) => setPromotionTypeId(e.target.value)} label={t('REPORT.FILTER.PROMOTION_USED.TYPE')} options={[{ value: 'ALL', name: t('REPORT.FILTER.ALL') }, ...promotionType]} defaultValue='ALL' />
                        </Grid>
                        <Grid xs={12} sm={6} item md={3} xl={2}>
                            <SelectInput onChange={(e) => setPromotionId(e.target.value)} label={t('REPORT.FILTER.PROMOTION_USED.PROMOTION')} options={[{ value: 'ALL', name: t('REPORT.FILTER.ALL') }, ...optionPromotion]} defaultValue="ALL" />
                        </Grid>
                    </Grid>
                    <Grid container spacing="16px" pt="16px">
                        <Grid xs={12} sm={6} item md={3} xl={2}>
                            <SelectInput onChange={(e) => {
                                if (e.target.value === 'EXTRA,DISCOUNT') setTypeName(t('REPORT.FILTER.PROMOTION_USED.ALL'))
                                else if (e.target.value === 'EXTRA') setTypeName(t('REPORT.FILTER.PROMOTION_USED.EXTRA'))
                                else if (e.target.value === 'DISCOUNT') setTypeName(t('REPORT.FILTER.PROMOTION_USED.DISCOUNT'))
                                setType(e.target.value)
                            }
                            }
                                label={t('REPORT.FILTER.PROMOTION_USED.FORMAT')}
                                defaultValue='EXTRA,DISCOUNT' options={[
                                    { value: 'EXTRA,DISCOUNT', name: t('REPORT.FILTER.PROMOTION_USED.ALL') },
                                    { value: 'EXTRA', name: t('REPORT.FILTER.PROMOTION_USED.EXTRA') },
                                    { value: 'DISCOUNT', name: t('REPORT.FILTER.PROMOTION_USED.DISCOUNT') }
                                ]} />
                        </Grid>
                        <Grid xs={12} sm={6} item md={3} xl={2}>
                            <InputSecondNewRangePicker
                                inputHeight={40}
                                value={rangeDate || ['', '']}
                                onchange={(val: any) => {
                                    if (_.isEmpty(val)) setRangeDate(['', ''])
                                    else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                                }}
                                label={t('REPORT.FILTER.PROMOTION_USED.DATE')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={6} xl={8}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <MainButton onClick={() => fetchPromotionList()} title={t('INPUT.SEARCH')} type="primary" variant="contained" style={{ width: '120px' }} />
                                <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
                                    if (e === 'Excel') handleExport()
                                    else if (e === 'PDF') {
                                        const style = document.createElement("style");
                                        style.innerHTML = `
                                                        @media print {
                                                            @page { size: landscape }
                                                        }
                                                        `;
                                        document.head.appendChild(style);
                                        window.print();
                                        document.head.removeChild(style);
                                    }
                                }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
                {/* id="print-table-container" ref={componentRef} */}
                <Box sx={{ overflowX: 'auto', overflowY: 'hidden', marginTop: '16px' }} >
                    <TableContainer style={{ boxShadow: 'none', borderTopLeftRadius: '10px', borderTopRightRadius: '10px' }}>
                        <Table aria-label="simple table" sx={{ width: '100%' }}>
                            <TableHead sx={{ backgroundColor: "#2F4269", color: 'white', border: '1px solid #2F4269' }}>
                                <TableRow>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', minWidth: '100px', textAlign: 'center' }}>{t('REPORT.TABLE.PROMOTION_USED.NO')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', textAlign: 'center' }}>{t('REPORT.TABLE.PROMOTION_USED.DATE_TIME')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', minWidth: '160px', textAlign: 'center' }}>{t('REPORT.TABLE.PROMOTION_USED.BILL_ID')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', minWidth: '100px', textAlign: 'center' }}>{t('REPORT.TABLE.PROMOTION_USED.PATIENT_CN_NUMBER')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', minWidth: '160px', textAlign: 'center' }}>{t('REPORT.TABLE.PROMOTION_USED.BRANCH_CN_NUMBER')}</TableCell>
                                    {snType && <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px' }}>{t('REPORT.TABLE.PROMOTION_USED.PATIENT_SN_CODE')}</TableCell>}
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px' }}>{t('REPORT.TABLE.PROMOTION_USED.PATIENT_NAME')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px' }}>{t('REPORT.TABLE.PROMOTION_USED.PROMOTION_TYPE')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px' }}>{t('REPORT.TABLE.PROMOTION_USED.PROMOTION')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', textAlign: 'center', minWidth: '160px' }}>{t('REPORT.TABLE.PROMOTION_USED.FORMAT_PROMOTION')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px' }}>{t('REPORT.TABLE.PROMOTION_USED.VALUE')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', textAlign: 'end' }}>{t('REPORT.TABLE.PROMOTION_USED.TOTAL_REDUCTION')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {promotionList.length === 0 ?
                                    <TableRow key={0} style={{ borderLeft: '1px solid rgba(224, 224, 224, 1)', borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                                        <TableCell colSpan={11} sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'center' }}>{t('NOT_FOUND')}</TableCell>
                                    </TableRow>
                                    :
                                    promotionList.map((data: any, index) => (
                                        <TableRow key={index} style={{ borderLeft: '1px solid rgba(224, 224, 224, 1)', borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'center' }}>{page * pageLimit - pageLimit + index + 1}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'center', minWidth: '190px' }}>{dateTimeToView(data?.date)}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'center' }}>{data?.receiptNumber || "-"}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'center' }}>{data?.cnNumber || "-"}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'center' }}>{data?.branchCnNumber || "-"}</TableCell>
                                            {snType && <TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data?.snCode || "-"}</TableCell>}
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, minWidth: '200px' }}>{data?.patientName || "-"}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, minWidth: '200px' }}>{data?.promotionTypeName || "-"}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, minWidth: '400px' }}>{data?.promotionName || '-'}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'center' }}>{data.type || '-'}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, minWidth: '150px' }}>{data?.promotionPrice}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'end', minWidth: '130px' }}>{numberFormat(data?.promotionDiscount) || '0.00'}</TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box marginTop="8px">
                        <PaginationTable
                            onChange={(_e, newPage) => setPage(newPage)}
                            onPerPageChange={(e) => {
                                setPageLimit(parseInt(e.target.value, 10))
                                setPage(1)
                            }}
                            page={page}
                            perPage={pageLimit}
                            totalPage={rowCount}
                        />
                    </Box>
                </Box>
                <Box width="75%" marginTop="16px">
                    <TableContainer style={{ boxShadow: 'none', borderTopLeftRadius: '10px', borderTopRightRadius: '10px' }}>
                        <Table aria-label="simple table" sx={{ width: '100%' }}>
                            <TableHead sx={{ backgroundColor: "#2F4269", border: '1px solid #2F4269' }}>
                                <TableRow>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', paddingX: '8px' }}>{t('REPORT.TABLE.PROMOTION_USED.PROMOTION_TYPE')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', paddingX: '8px' }}>{t('REPORT.TABLE.PROMOTION_USED.PROMOTION')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', paddingX: '8px' }}>{t('REPORT.TABLE.PROMOTION_USED.VALUE')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', textAlign: 'end', minWidth: '130px', paddingX: '8px' }}>{t('REPORT.TABLE.PROMOTION_USED.UNIT_COUNT')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', textAlign: 'end', paddingX: '8px' }}>{t('REPORT.TABLE.PROMOTION_USED.TOTAL_REDUCTION')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {promotion.length === 0 ?
                                    <TableRow key={0} style={{ borderLeft: '1px solid rgba(224, 224, 224, 1)', borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                                        <TableCell colSpan={11} sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'center' }}>{t('NOT_FOUND')}</TableCell>
                                    </TableRow>
                                    :
                                    <>
                                        {promotion.map((data: any, index) => (
                                            <TableRow key={index} style={{ borderLeft: '1px solid rgba(224, 224, 224, 1)', borderRight: '1px solid rgba(224, 224, 224, 1)', }}>
                                                <TableCell sx={{ fontSize: '16px', fontWeight: 400, verticalAlign: 'top', padding: '8px' }}>{data?.promotionTypeName || '-'}</TableCell>
                                                <TableCell sx={{ fontSize: '16px', fontWeight: 400, minWidth: '190px', verticalAlign: 'top', padding: '8px' }}>
                                                    {_.map(data?.promotions, (item, i: number) => (
                                                        <Typography marginTop={i !== 0 ? "16px" : 0}  >{item.promotionName || '-'}</Typography>
                                                    ))}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '16px', fontWeight: 400, verticalAlign: 'top', padding: '8px' }}>
                                                    {_.map(data?.promotions, (item, i: number) => (
                                                        <Typography marginTop={i !== 0 ? "16px" : 0} minWidth="140px" >{item.value || '-'}</Typography>
                                                    ))}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'end', verticalAlign: 'top', padding: '8px' }}>
                                                    {_.map(data?.promotions, (item, i: number) => (
                                                        <Typography marginTop={i !== 0 ? "16px" : 0} >{item.countUsed || '-'}</Typography>
                                                    ))}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'end', verticalAlign: 'top', padding: '8px' }}>
                                                    {_.map(data?.promotions, (item, i: number) => (
                                                        <Typography marginTop={i !== 0 ? "16px" : 0} >{item.sumDiscount || 0}</Typography>
                                                    ))}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow style={{ borderLeft: '1px solid rgba(224, 224, 224, 1)', borderRight: '1px solid rgba(224, 224, 224, 1)', backgroundColor: '#E5E5E5' }}>
                                            <TableCell colSpan={3} align="right" sx={{ fontSize: '16px', fontWeight: 400 }}>
                                                <Typography>{t('REPORT.TABLE.PROMOTION_USED.TOTAL')}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ padding: '8px', fontSize: '16px', fontWeight: 400 }} align="right">{_.sum(_.map(promotion, (item: any) => (_.sumBy(item.promotions, 'countUsed'))))}</TableCell>
                                            <TableCell sx={{ padding: '8px', fontSize: '16px', fontWeight: 400 }} align="right">{numberFormat(_.sum(_.map(promotion, (item: any) => (_.sumBy(item.promotions, 'sumDiscount')))), 2)}</TableCell>
                                        </TableRow>
                                    </>
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box >

            <Box
                id="print-table-container" ref={componentRef}
            >
                <Typography fontSize="14px" fontWeight={400}>{t('REPORT.TABLE.PROMOTION_USED.TITLE')}</Typography>
                <Box display='flex' flexDirection="row" alignItems="center" gap="16px">
                    <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.BRANCH')}: {`${_.get(_.find(branch, { value: branchId }), 'name')}`}</Typography>
                    <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.START')}: {rangeDate[0] ? toBuddhistYear(moment(rangeDate[0]), 'DD/MM/YYYY') : '-'}</Typography>
                    <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.TO')}: {rangeDate[1] ? toBuddhistYear(moment(rangeDate[1]), 'DD/MM/YYYY') : '-'}</Typography>
                    <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.PROMOTION_USED.TYPE')}: {promotionTypeId === "ALL" ? t('REPORT.FILTER.ALL') : `${_.get(_.find(promotionType, { value: promotionTypeId }), 'name')}`}</Typography>
                    <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.PROMOTION_USED.PROMOTION')}: {promotionId === "ALL" ? t('REPORT.FILTER.ALL') : `${_.get(_.find(optionPromotion, { value: promotionId }), 'name')}`}</Typography>
                    <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.PROMOTION_USED.FORMAT')}: {typeName === "" ? t('REPORT.FILTER.ALL') : typeName}</Typography>
                </Box>

                <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse', marginTop: '16px' }}>
                    <thead>
                        <tr style={{ textAlign: 'center', fontSize: '12px', fontWeight: 700 }}>
                            {[t('REPORT.TABLE.DF.#NO'), t('วันที่'), t('เลขที่ใบเสร็จ'), t('รหัสผู้ป่วย'), t('รหัสผู้ป่วยในสาขา'), t('ชื่อผู้ป่วย'), t('ประเภทโปรโมชัน'), t('โปรโมชัน'), t('รูปแบบโปรโมชัน'), t('มูลค่า'), t('ยอดลด (บาท)')].map((h, i) => (
                                <th key={i} style={{ fontSize: '10px', border: '1px solid black', padding: '6px', fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}> {h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {promotionExport.length === 0 ? (
                            <tr>
                                <td colSpan={11} style={{ border: '1px solid black', padding: '6px', fontSize: 14, verticalAlign: 'top', textAlign: 'center' }}>
                                    {t('NOT_FOUND')}
                                </td>
                            </tr>
                        ) : (
                            promotionExport?.map((data: any, index: number) => (
                                <tr key={index}>
                                    <td style={{ fontSize: '9.5px', fontWeight: 500, border: '1px solid black', textAlign: 'center', verticalAlign: 'top', width: '50px' }}>{index + 1}</td>
                                    <td style={{ fontSize: '9.5px', fontWeight: 500, border: '1px solid black', textAlign: 'center', verticalAlign: 'top', width: '50px' }}>{data.date ? toBuddhistYear(moment(data.date), 'DD/MM/YYYY') : '-'}</td>
                                    <td style={{ fontSize: '9.5px', fontWeight: 500, border: '1px solid black', textAlign: 'center', verticalAlign: 'top', width: '50px' }}>{data?.receiptNumber}</td>
                                    <td style={{ fontSize: '9.5px', fontWeight: 500, border: '1px solid black', textAlign: 'center', verticalAlign: 'top', width: '50px' }}>{data.cnNumber}</td>
                                    <td style={{ fontSize: '9.5px', fontWeight: 500, border: '1px solid black', textAlign: 'center', verticalAlign: 'top', width: '50px' }}>{data?.branchCnNumber}</td>
                                    <td style={{ fontSize: '9.5px', fontWeight: 500, border: '1px solid black', textAlign: 'center', verticalAlign: 'top' }}>{data?.patientName}</td>
                                    <td style={{ fontSize: '9.5px', fontWeight: 500, border: '1px solid black', textAlign: 'center', verticalAlign: 'top' }}>{data?.promotionTypeName}</td>
                                    <td style={{ fontSize: '9.5px', fontWeight: 500, border: '1px solid black', textAlign: 'center', verticalAlign: 'top' }}>{data?.promotionName}</td>
                                    <td style={{ fontSize: '9.5px', fontWeight: 500, border: '1px solid black', textAlign: 'center', verticalAlign: 'top', width: '50px' }}>{data?.type}</td>
                                    <td style={{ fontSize: '9.5px', fontWeight: 500, border: '1px solid black', textAlign: 'center', verticalAlign: 'top' }}>{data?.promotionPrice}</td>
                                    <td style={{ fontSize: '9.5px', fontWeight: 500, border: '1px solid black', textAlign: 'right', verticalAlign: 'top', width: '50px' }}>{numberFormat(data.promotionDiscount, 2)}</td>
                                </tr>
                            )))}
                    </tbody>
                </table>


                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16, fontSize: 9.5, tableLayout: 'fixed' }}>
                    <thead>
                        <tr>
                            {[t('ประเภทโปรโมชัน'), t('โปรโมชัน'), t('มูลค่า'), t('จำนวน (ครั้ง)'), t('ยอดลด (บาท)')].map((h, i) => (
                                <th key={i} style={{ fontSize: '12px', border: '1px solid black', padding: '6px', fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}> {h}</th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {promotion.length === 0 ? (
                            <tr>
                                <td colSpan={5} style={{ border: '1px solid black', padding: '6px', fontSize: 14, verticalAlign: 'top', textAlign: 'center' }}>
                                    {t('NOT_FOUND')}
                                </td>
                            </tr>
                        ) : (
                            promotion?.map((group: any, gi: any) => {
                                const rows = group.promotions || []
                                return rows.map((promo: any, pi: any) => (
                                    <tr key={`${gi}-${pi}`}>
                                        {pi === 0 && (
                                            <td rowSpan={rows.length} style={{ fontSize: '9.5px', border: '1px solid black', padding: '6px', verticalAlign: 'middle', textAlign: 'center', fontWeight: 500 }}>{group.promotionTypeName} </td>
                                        )}
                                        <td style={{ fontSize: '9.5px', border: '1px solid black', padding: '6px', verticalAlign: 'top', textAlign: 'left', fontWeight: 500 }}>{promo.promotionName}</td>
                                        <td style={{ fontSize: '9.5px', border: '1px solid black', padding: '6px', verticalAlign: 'top', textAlign: 'right', fontWeight: 500, width: '100px' }}> {promo.value} </td>
                                        <td style={{ fontSize: '9.5px', border: '1px solid black', padding: '6px', verticalAlign: 'top', textAlign: 'right', fontWeight: 500, width: '100px' }}> {promo.countUsed} </td>
                                        <td style={{ fontSize: '9.5px', border: '1px solid black', padding: '6px', verticalAlign: 'top', textAlign: 'right', fontWeight: 50, width: '100px' }}> {numberFormat(promo.sumDiscount, 2)}</td>
                                    </tr>
                                ))
                            })
                        )}
                        {promotion.length !== 0 && <tr>
                            <td colSpan={3} style={{ border: '1px solid black', padding: '6px', textAlign: 'right', fontWeight: 700 }}>{t('REPORT.TABLE.PROMOTION_USED.TOTAL')}</td>
                            <td style={{ border: '1px solid black', padding: '6px', textAlign: 'right', fontWeight: 700 }}>
                                {_.sum(promotion.map((g: any) => _.sumBy(g.promotions, 'countUsed')))}
                            </td>
                            <td style={{ border: '1px solid black', padding: '6px', textAlign: 'right', fontWeight: 700 }}>
                                {numberFormat(_.sum(promotion.map((g: any) => _.sumBy(g.promotions, 'sumDiscount'))), 2)}
                            </td>
                        </tr>}
                    </tbody>
                </table>
            </Box>
        </Box >
    )
}